import { Badge } from "../../../components/ui/Badge";
import { CatDomainScore, ClinicalStats } from "../../../api-helpers/ClinicalStatsTypes";
import { ClinicalStatsHookStatus } from "../../../hooks/useClinicalStats";

interface RespiratoryStatusBandProps {
  status: ClinicalStatsHookStatus;
  stats: ClinicalStats | null;
}

const CAT_DOMAIN_LABELS: Record<CatDomainScore["domain"], string> = {
  cough: "Cough",
  phlegm: "Phlegm",
  chestTightness: "Chest tightness",
  breathlessness: "Breathlessness",
  activitiesAtHome: "Activity limitation",
  confidenceLeavingHome: "Confidence leaving home",
  sleep: "Sleep",
  energy: "Energy",
};

// The 4-5 "high impact" segment-colour threshold is a design assumption
// from the redesign handoff, pending clinical confirmation - see
// design_handoff_patient_summary README "Open Questions".
const CAT_HIGH_IMPACT_THRESHOLD = 4;

function yesNoLabel(value: boolean | null): string | null {
  if (value == null) return null;
  return value ? "Yes" : "No";
}

// mMRC/CAT score severity tiers (respiratory-status-color-coding-handoff.md).
// Number color is darker than the bar fill so the number stays readable on
// white; only use the bar color for text.
type ScoreTier = "green" | "yellow" | "red";

const SCORE_TIER_COLORS: Record<ScoreTier, { ink: string; bar: string }> = {
  green: { ink: "#2E7D4F", bar: "#3FA66A" },
  yellow: { ink: "#8A6400", bar: "#E3A42E" },
  red: { ink: "#B3261E", bar: "#D6453A" },
};

const clampScore = (score: number, max: number) =>
  Math.min(Math.max(score, 0), max);

// mMRC 0 is treated as green along with 1, per the handoff's assumption
// (open question - pending clinical confirmation).
const mmrcTier = (score: number): ScoreTier => {
  if (score >= 4) return "red";
  if (score >= 2) return "yellow";
  return "green";
};

const catTier = (score: number): ScoreTier => {
  if (score >= 21) return "red";
  if (score >= 10) return "yellow";
  return "green";
};

// Score number + severity meter, shared by the mMRC and CAT cards.
const ScoredMetric = ({
  score,
  max,
  tier,
  ariaLabel,
}: {
  score: number;
  max: number;
  tier: ScoreTier;
  ariaLabel: string;
}) => {
  const clamped = clampScore(score, max);
  const { ink, bar } = SCORE_TIER_COLORS[tier];

  return (
    <>
      <div className="ps-metric-card__value-row ps-metric-card__value-row--scored">
        <span
          className="ps-metric-card__value ps-metric-card__value--scored"
          style={{ color: ink }}
        >
          {clamped}
        </span>
        <span className="ps-metric-card__unit ps-metric-card__unit--scored">
          of {max}
        </span>
      </div>
      <meter
        className="ps-metric-card__meter"
        min={0}
        max={max}
        value={clamped}
        aria-label={ariaLabel}
        style={{ "--meter-fill": bar } as React.CSSProperties}
      />
    </>
  );
};

const SkeletonBand = () => (
  <div>
    <h2 className="ps-band-title">Respiratory status</h2>
    <div className="ps-respiratory-grid">
      <div className="ps-metrics-grid">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="ps-card ps-skeleton-block" style={{ height: 128 }} />
        ))}
      </div>
      <div className="ps-card ps-skeleton-block" style={{ minHeight: 260 }} />
    </div>
  </div>
);

// "Respiratory status" band (design_handoff_patient_summary, option 2a):
// four key metric cards plus the CAT symptom breakdown. Summary-tab-only -
// the appointment-charting page's ClinicianAiSummaryPanel does not render
// any of this. No data (new patient, never assessed) or a failed fetch both
// just drop the whole band - unlike NeedsAttentionBand, there's no count/
// badge semantics riding on "empty" here, so hiding it is the simpler,
// requested behavior rather than an "All caught up"-style empty state.
export const RespiratoryStatusBand = ({ status, stats }: RespiratoryStatusBandProps) => {
  if (status === "error") return null;
  if (status === "loading") return <SkeletonBand />;
  if (!stats) return null;

  const { mmrcGrade, catScore, respiratorySupport, hospitalizationUtilization } = stats;
  const hasAnyMetric =
    mmrcGrade || catScore || respiratorySupport || hospitalizationUtilization;

  if (!hasAnyMetric) return null;

  const sortedDomains = catScore
    ? [...catScore.domains].sort((a, b) => b.score - a.score)
    : [];

  return (
    <div>
      <h2 className="ps-band-title">Respiratory status</h2>
      <div className="ps-respiratory-grid">
        <div className="ps-metrics-grid">
        {mmrcGrade && (
          <div className="ps-card ps-metric-card">
            <div className="ps-metric-card__label-row">
              <span className="ps-eyebrow">mMRC grade</span>
            </div>
            <ScoredMetric
              score={mmrcGrade.grade}
              max={4}
              tier={mmrcTier(mmrcGrade.grade)}
              ariaLabel={`mMRC grade ${clampScore(mmrcGrade.grade, 4)} of 4`}
            />
            {mmrcGrade.label && <div className="ps-metric-card__note">{mmrcGrade.label}</div>}
          </div>
        )}

        {catScore && (
          <div className="ps-card ps-metric-card">
            <div className="ps-metric-card__label-row">
              <span className="ps-eyebrow">CAT score</span>
            </div>
            <ScoredMetric
              score={catScore.totalScore}
              max={40}
              tier={catTier(catScore.totalScore)}
              ariaLabel={`CAT score ${clampScore(catScore.totalScore, 40)} of 40`}
            />
          </div>
        )}

        {respiratorySupport && (
          <div className="ps-card ps-metric-card">
            <div className="ps-metric-card__label-row">
              <span className="ps-eyebrow">Respiratory support</span>
              <Badge
                tone="neutral"
                title="Self-reported on the patient's most recent assessment, not a verified device order."
              >
                Self-reported
              </Badge>
            </div>
            {respiratorySupport.usesOxygen != null && (
              <div className="ps-metric-card__value-row">
                <span className="ps-metric-card__value">
                  {respiratorySupport.oxygenLitersPerMinute != null
                    ? respiratorySupport.oxygenLitersPerMinute
                    : yesNoLabel(respiratorySupport.usesOxygen)}
                </span>
                {respiratorySupport.oxygenLitersPerMinute != null && (
                  <span className="ps-metric-card__unit">L/min Oxygen</span>
                )}
              </div>
            )}
            {respiratorySupport.oxygenFrequency && (
              <div className="ps-metric-card__note">{respiratorySupport.oxygenFrequency}</div>
            )}
            {respiratorySupport.usesCpapOrBipap != null && (
              <div className="ps-metric-card__note">
                CPAP/BiPAP: {yesNoLabel(respiratorySupport.usesCpapOrBipap)}
                {respiratorySupport.usesCpapOrBipap &&
                  respiratorySupport.cpapCompliant != null && (
                    <> · Compliant: {yesNoLabel(respiratorySupport.cpapCompliant)}</>
                  )}
              </div>
            )}
          </div>
        )}

        {hospitalizationUtilization && (
          <div className="ps-card ps-metric-card">
            <div className="ps-metric-card__label-row">
              <span className="ps-eyebrow">Hospital utilization</span>
              {hospitalizationUtilization.confidence === "self-reported" && (
                <Badge
                  tone="neutral"
                  title="Self-reported on the patient's most recent assessment, not a verified event count."
                >
                  Self-reported
                </Badge>
              )}
            </div>
            <div className="ps-metric-card__value-row">
              {hospitalizationUtilization.hospitalizationCountLast12Months != null ? (
                <>
                  <span className="ps-metric-card__value">
                    {hospitalizationUtilization.hospitalizationCountLast12Months}
                  </span>
                  <span className="ps-metric-card__unit">events / 12 mo</span>
                </>
              ) : (
                hospitalizationUtilization.hospitalizedLast12Months != null && (
                  <span className="ps-metric-card__value">
                    {yesNoLabel(hospitalizationUtilization.hospitalizedLast12Months)}
                  </span>
                )
              )}
            </div>
            {hospitalizationUtilization.erVisitLast12Months != null && (
              <div className="ps-metric-card__note">
                ED visit (12mo): {yesNoLabel(hospitalizationUtilization.erVisitLast12Months)}
              </div>
            )}
          </div>
        )}
      </div>

      {catScore && sortedDomains.length > 0 && (
        <div className="ps-card">
          <div className="ps-cat-card__header">
            <span className="ps-section-title">CAT Symptom Breakdown</span>
            <span className="ps-cat-card__hint">Highest first</span>
          </div>
          <div className="ps-cat-card__grid">
            {sortedDomains.map((domain) => (
              <div key={domain.domain}>
                <div className="ps-cat-item__top">
                  <span>{CAT_DOMAIN_LABELS[domain.domain]}</span>
                  <span className="ps-cat-item__score">{domain.score}/5</span>
                </div>
                <div className="ps-cat-item__scale">
                  {Array.from({ length: 5 }).map((_, i) => {
                    const filled = i < domain.score;
                    const highImpact = domain.score >= CAT_HIGH_IMPACT_THRESHOLD;
                    return (
                      <span
                        key={i}
                        className={`ps-cat-item__segment${
                          filled ? (highImpact ? " ps-cat-item__segment--high" : " ps-cat-item__segment--low") : ""
                        }`}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
        )}
      </div>
    </div>
  );
};
